import React, { useContext } from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { css } from '@emotion/react';
import uuid from 'react-uuid';

// redux
import { getFlatCourseAliases } from 'redux/selectors/course';
import {
  createAiCharacterObjection,
  deleteAiCharacterObjection,
  updateAiCharacterObjection,
} from 'redux/actions/ai-role-play';

import { doubleSpacing, halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import {
  ReactComponent as AICharacter,
} from 'styles/icons/ai-characters.svg';
import ObjectiveInputList from './objective-input-list';
import { AIPersonalities } from '..';
import EditAiInstructionContext from './context';

const styles = css`
  .learner-role-input {
    width: calc(50% - ${halfSpacing}px);
  }
  .gap-2 {
    gap: ${standardSpacing}px;
  }
  .personality-dropdown-button {
    width: calc(50% - ${halfSpacing}px);
    .bs4-dropdown {
      .title {
        height: 45px;
    }
  }
`;

const CharacterSetting = () => {
  const params = useLecturePageParams();
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const personalities = [AIPersonalities.NEUTRAL, AIPersonalities.IRRITABLE, AIPersonalities.DISINTERESTED];

  const {
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { aiRolePlayId } = useContext(EditAiInstructionContext) || {};
  const [showDropdown, setShowDropdown] = React.useState(false);

  const items: NvDropdownOption[] = personalities.map((personality, index) => ({
    id: index,
    text: personality,
    type: 'text',
    textClassName: 'text-large-body',
    callback: () => {
      setValue('aiCharacterPersonality', personality, { shouldDirty: true });
      trigger('aiCharacterPersonality');
      setShowDropdown(false);
    },
  }));
  const initialIndex = personalities.indexOf(getValues().aiCharacterPersonality);
  const [selectedIndex, setSelectedIndex] = React.useState(initialIndex);
  const [isAddingNewAiObjective, setIsAddingNewAiObjective] = React.useState(false);

  const { control } = useFormContext();
  const dispatch = useDispatch();

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    name: 'aiCharacterObjections',
    control,
    keyName: 'id',
  });

  const handleAddAiObjection = () => {
    if (aiRolePlayId) {
      setIsAddingNewAiObjective(true);
      dispatch(createAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        text: '',
      })).then((res) => {
        if (res) {
          append({ id: res.payload.id, text: '' });
        }
      }).finally(() => {
        setIsAddingNewAiObjective(false);
      });
    } else {
      append({ id: uuid(), text: '' });
    }
  };

  const handleRemoveAiObjection = (aiCharacterObjectionId) => {
    if (fields.length <= 0) return;
    const index = fields.findIndex(field => field.id === aiCharacterObjectionId);
    remove(index);
    if (aiRolePlayId && aiCharacterObjectionId) {
      dispatch(deleteAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        aiCharacterObjectionId,
      }));
    }
  };

  const handleUpdateAiObjection = (aiCharacterObjectionId, value, fieldError) => {
    if (fieldError && fieldError.text) {
      return;
    }
    if (!aiRolePlayId || !aiCharacterObjectionId) return;
    dispatch(updateAiCharacterObjection({
      catalogId: params.catalogId,
      aiRolePlayId,
      aiCharacterObjectionId,
      text: value,
    }));
  };

  return (
    <div css={styles}>
      <div className='d-flex flex-column'>
        <div className='d-flex justify-content-center mt-8'>
          <AICharacter
            height={doubleSpacing}
            width={doubleSpacing}
          />
        </div>
        <p className='page-title text-large mb-4 mt-2 mx-auto text-black lh-35'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.THE_CHARACTERS()}
        </p>
      </div>
      <div>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARNER_ROLE(courseAliases)}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.WHAT_ROLE_PLAYING()}
        </p>
        <NvTextInput
          withForm
          name='learnerRole'
          className='border-gray-5 bg-white mt-2 learner-role-input required-input'
          inputClassName='text-large-body lh-25'
          required
        />
      </div>
      <div className='d-flex w-100 mt-4 gap-2'>
        <div className='w-50'>
          <p className='bold text-large-body mb-0 lh-25'>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_ROLE()}
          </p>
          <NvTextInput
            withForm
            name='aiCharacterRole'
            className='border-gray-5 bg-white mt-2 required-input'
            inputClassName='text-large-body lh-25'
            required
          />
        </div>
        <div className='w-50'>
          <p className='bold text-large-body mb-0 lh-25'>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_NAME()}
          </p>
          <NvTextInput
            withForm
            name='aiCharacterName'
            className='border-gray-5 bg-white mt-2 required-input'
            required
            inputClassName='text-large-body lh-25'
          />
        </div>
      </div>
      <div className='mt-4'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PERSONALITY()}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.TELL_AI_TO_BEHAVE()}
        </p>
        <div className='personality-dropdown-button text-large-body'>
          <NvDropdown
            showSelectedIndicator
            buttonStyle={NvDropdownButtonStyle.FORM}
            items={items}
            initialIndex={selectedIndex}
            show={showDropdown}
            menuClassName='w-100'
            onToggle={(isOpen) => setShowDropdown(isOpen)}
          />
        </div>
      </div>
      <div className='mt-4'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_OBJECTIONS()}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.TELL_AI_TO_SAY()}
        </p>
        <ObjectiveInputList
          name='aiCharacterObjections'
          fields={fields}
          handleAddField={handleAddAiObjection}
          handleRemoveField={handleRemoveAiObjection}
          handleBlurField={handleUpdateAiObjection}
          isAddingField={isAddingNewAiObjective}
          maxFieldCount={5}
        />
      </div>
    </div>
  );
};

export default CharacterSetting;
